import React from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    BooleanField,
    DateField,
    useRecordContext,
} from 'react-admin';
import { Card, CardContent, Typography, Box, Chip, Grid } from '@mui/material';
import config from '../config/config';
import { Labeled } from 'react-admin';

const StatusPanel = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
                Verification Status
            </Typography>
            <Box display="flex" gap={2}>
                <Chip
                    label={record.is_valid ? 'Valid Authentication' : 'Invalid Authentication'}
                    color={record.is_valid ? 'success' : 'error'}
                    sx={{ fontSize: '1rem', py: 2 }}
                />
                <Chip
                    label={record.is_tt_closed ? 'No Tampering Detected' : 'Tampering Detected'}
                    color={record.is_tt_closed ? 'success' : 'error'}
                    sx={{ fontSize: '1rem', py: 2 }}
                />
            </Box>
        </Box>
    );
};

const hasGpsFix = (record) =>
    record &&
    typeof record.gps_lat === 'number' &&
    typeof record.gps_lon === 'number';

const LocationDetails = () => {
    const record = useRecordContext();
    if (!record) return null;

    const hasIp = record.location_city && record.location_country;
    const hasGps = hasGpsFix(record);
    if (!hasIp && !hasGps) return null;

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
                Location Information
            </Typography>
            <Grid container spacing={2}>
                {hasIp && (
                    <>
                        <Grid item xs={12} md={6}>
                            <Labeled label="City (IP)">
                                <TextField source="location_city" />
                            </Labeled>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Labeled label="Country (IP)">
                                <TextField source="location_country" />
                            </Labeled>
                        </Grid>
                    </>
                )}
                {hasGps && (
                    <>
                        <Grid item xs={12} md={4}>
                            <Labeled label="GPS Latitude">
                                <Typography variant="body2">{record.gps_lat.toFixed(6)}</Typography>
                            </Labeled>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Labeled label="GPS Longitude">
                                <Typography variant="body2">{record.gps_lon.toFixed(6)}</Typography>
                            </Labeled>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Labeled label="Accuracy">
                                <Typography variant="body2">
                                    {typeof record.gps_accuracy === 'number'
                                        ? `${record.gps_accuracy.toFixed(1)} m`
                                        : 'unknown'}
                                </Typography>
                            </Labeled>
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
};

const LocationMap = () => {
    const record = useRecordContext();
    const [ipCoordinates, setIpCoordinates] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    const gpsAvailable = hasGpsFix(record);

    React.useEffect(() => {
        if (gpsAvailable) {
            setLoading(false);
            return;
        }
        if (!record || !record.location_city || !record.location_country) {
            setLoading(false);
            return;
        }

        const geocodeLocation = async () => {
            try {
                const query = encodeURIComponent(`${record.location_city}, ${record.location_country}`);
                const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`);
                const data = await response.json();

                if (data && data.length > 0) {
                    setIpCoordinates({
                        lat: parseFloat(data[0].lat),
                        lon: parseFloat(data[0].lon)
                    });
                }
            } catch (error) {
                console.error('Geocoding error:', error);
            } finally {
                setLoading(false);
            }
        };

        geocodeLocation();
    }, [record, gpsAvailable]);

    if (!record) return null;
    if (!gpsAvailable && (!record.location_city || !record.location_country)) return null;

    const coordinates = gpsAvailable
        ? { lat: record.gps_lat, lon: record.gps_lon }
        : ipCoordinates;
    const source = gpsAvailable ? 'GPS' : 'IP-derived';
    const accuracyMeters = gpsAvailable && typeof record.gps_accuracy === 'number'
        ? record.gps_accuracy
        : null;

    const halfSpan = gpsAvailable
        ? Math.max(0.001, (accuracyMeters || 50) / 111000 * 5)
        : 0.05;

    let embedUrl = 'https://www.openstreetmap.org/export/embed.html?layer=mapnik';
    if (coordinates) {
        const bbox = [
            coordinates.lon - halfSpan,
            coordinates.lat - halfSpan,
            coordinates.lon + halfSpan,
            coordinates.lat + halfSpan,
        ].join(',');
        embedUrl += `&bbox=${bbox}&marker=${coordinates.lat},${coordinates.lon}`;
    }

    const mapUrl = coordinates
        ? `https://www.openstreetmap.org/?mlat=${coordinates.lat}&mlon=${coordinates.lon}#map=16/${coordinates.lat}/${coordinates.lon}`
        : `https://www.openstreetmap.org/search?query=${encodeURIComponent(record.location_city + ', ' + record.location_country)}`;

    const captionParts = [];
    if (gpsAvailable) {
        captionParts.push(`GPS ${record.gps_lat.toFixed(6)}, ${record.gps_lon.toFixed(6)}`);
        if (accuracyMeters !== null) {
            captionParts.push(`±${accuracyMeters.toFixed(1)} m`);
        }
    }
    if (record.location_city && record.location_country) {
        captionParts.push(`IP: ${record.location_city}, ${record.location_country}`);
    }

    return (
        <Card sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Location Map ({source})
                </Typography>
                <Box sx={{ position: 'relative', height: 400, mb: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" color="textSecondary">
                                Loading map...
                            </Typography>
                        </Box>
                    ) : coordinates ? (
                        <iframe
                            src={embedUrl}
                            width="100%"
                            height="100%"
                            style={{ border: 0, borderRadius: 4 }}
                            title="Location Map"
                        />
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" color="textSecondary">
                                No coordinates available
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {captionParts.join(' · ')}
                    </Typography>
                    <Typography variant="body2">
                        <a
                            href={mapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'primary.main', textDecoration: 'none' }}
                        >
                            Open in OpenStreetMap →
                        </a>
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

const NETWORK_NAMES = {
    1: 'Ethereum Mainnet',
    3: 'Ropsten Testnet',
    4: 'Rinkeby Testnet',
    5: 'Goerli Testnet',
    11155111: 'Ethereum Sepolia Testnet',
    137: 'Polygon Mainnet',
    80001: 'Polygon Mumbai Testnet',
    80002: 'Polygon Amoy Testnet',
};

const NFTDetails = () => {
    const record = useRecordContext();
    if (!record?.nft || !record?.metadata) return null;

    // Organize NFT metadata attributes into a more readable format
    const attributesMap = record.metadata.attributes?.reduce((acc, attr) => {
        acc[attr.trait_type] = attr.value;
        return acc;
    }, {}) || {};

    const networkName = NETWORK_NAMES[record.nft.chain_id] || `Unknown Network (${record.nft.chain_id})`;

    return (
        <Card sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    NFT Information
                </Typography>
                
                <Grid container spacing={3}>
                    {/* NFT Image */}
                    <Grid item xs={12} md={4}>
                        <Box
                            component="img"
                            src={record.image_url && !record.image_url.startsWith('http') ? `${config.s3BaseUrl}/${record.image_url}` : record.image_url}
                            alt={record.metadata.name}
                            sx={{
                                width: '100%',
                                height: 'auto',
                                maxHeight: 350,
                                objectFit: 'contain',
                                borderRadius: 1,
                                boxShadow: 1,
                            }}
                        />
                    </Grid>

                    {/* NFT Details */}
                    <Grid item xs={12} md={8}>
                        <Typography variant="h5" gutterBottom>
                            {record.metadata.name}
                        </Typography>
                        
                        <Typography variant="body1" color="textSecondary" paragraph>
                            {record.metadata.description}
                        </Typography>

                        <Grid container spacing={2}>
                            {/* Contract Information */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
                                    Contract Details
                                </Typography>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Labeled label="Network">
                                            <Typography variant="body2">{networkName}</Typography>
                                        </Labeled>
                                    </Grid>
                                    
                                    <Grid item xs={6}>
                                        <Labeled label="Symbol">
                                            <Typography variant="body2">{record.nft.contract_symbol}</Typography>
                                        </Labeled>
                                    </Grid>
                                    
                                    {/* <Grid item xs={12}>
                                        <Labeled label="Name">
                                            <Typography variant="body2">{record.nft.contract_name}</Typography>
                                        </Labeled>
                                    </Grid> */}
                                    
                                    <Grid item xs={6}>
                                        <Labeled label="Address">
                                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                                {record.nft.contract_address}
                                            </Typography>
                                        </Labeled>
                                    </Grid>
                                    
                                    <Grid item xs={6}>
                                        <Labeled label="Token ID">
                                            <Typography variant="body2">{record.nft.token_id}</Typography>
                                        </Labeled>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Wine Details */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>
                                    Wine Details
                                </Typography>
                                <Grid container spacing={2}>
                                    {Object.entries(attributesMap).map(([key, value]) => (
                                        <Grid item xs={12} sm={6} key={key}>
                                            <Typography variant="body2">
                                                <strong>{key}:</strong> {value}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

const TechnicalDetails = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <Card sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Technical Details
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                            CMAC
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontFamily: 'monospace',
                                wordBreak: 'break-all'
                            }}
                        >
                            {record.cmac}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                            PICC Data
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontFamily: 'monospace',
                                wordBreak: 'break-all'
                            }}
                        >
                            {record.p}
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export const VerificationShow = () => (
    <Show>
        <SimpleShowLayout>
            <StatusPanel />
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Basic Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Labeled label="Tag UID">
                            <TextField source="uid" />
                        </Labeled>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Labeled label="Provenance">
                            <TextField source="provenance_id" />
                        </Labeled>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Labeled label="Message">
                            <TextField source="message" />
                        </Labeled>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Labeled label="Verification Time">
                            <DateField source="timestamp" showTime />
                        </Labeled>
                    </Grid>
                </Grid>
            </Box>
            <LocationDetails />
            {/* <TechnicalDetails /> */}
            <NFTDetails />
            <LocationMap />
        </SimpleShowLayout>
    </Show>
);

export default VerificationShow;